import { FileText } from "lucide-react";
import { useNavigate } from "react-router-dom";

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@needl-ai/common/components/ui/tooltip";
import { useGetPathPrefix } from "@needl-ai/common/workspace";

export const ArtifactReportButton = ({
  isStreaming = false,
  isLoading = false,
  hasIncompleteAttachments = false,
  isCollapsed = false,
}: ArtifactReportButtonProps) => {
  const navigate = useNavigate();
  const pathPrefix = useGetPathPrefix();

  const isDisabled = isStreaming || isLoading || hasIncompleteAttachments;

  const buttonClass = !isDisabled
    ? `${
        isCollapsed
          ? "w-12 h-12 rounded-lg flex items-center justify-center"
          : "w-full h-12 rounded-lg flex items-center gap-4 px-4"
      } transition-colors font-normal hover:font-medium active:font-medium text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 active:text-gray-900 dark:text-gray-200 dark:hover:bg-neutral-800 dark:hover:text-white dark:active:text-white`
    : `${
        isCollapsed
          ? "w-12 h-12 rounded-lg flex items-center justify-center"
          : "w-full h-12 rounded-lg flex items-center gap-4 px-4"
      } transition-colors font-normal text-sm opacity-75 cursor-not-allowed text-gray-400`;

  const handleClick = () => {
    if (!isDisabled) {
      navigate(`${pathPrefix}/report-automation`);
    }
  };

  const tooltipText =
    isStreaming || isLoading
      ? "Please wait while processing..."
      : hasIncompleteAttachments
        ? "Complete file uploads before accessing reports"
        : "Generate Reports";

  if (isCollapsed) {
    return (
      <div className="px-2">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={handleClick}
                className={buttonClass}
                disabled={isDisabled}
                aria-label={tooltipText}
                data-testid="report-button"
              >
                <FileText
                  size={20}
                  className={
                    !isDisabled
                      ? "text-gray-700 dark:text-white"
                      : "text-gray-500"
                  }
                />
              </button>
            </TooltipTrigger>
            <TooltipContent side="right">
              <p>{tooltipText}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    );
  }

  return (
    <div className="px-2 ml-2">
      <button
        onClick={handleClick}
        className={buttonClass}
        disabled={isDisabled}
        title={tooltipText}
        data-testid="report-button"
      >
        <FileText size={20} />
        <span>Automation Reports</span>
      </button>
    </div>
  );
};

type ArtifactReportButtonProps = {
  isStreaming?: boolean;
  isLoading?: boolean;
  hasIncompleteAttachments?: boolean;
  isCollapsed?: boolean;
};
